import React, { useEffect, useMemo, useState } from "react";
import ApiClient from "../api/ApiClient";
import { useAuth } from "../contexts/AuthContext";
import "./ModelSpreadsheet.scss";

// Convert a 0xAARRGGBB ARGB value from Constants.ColorTable into a CSS rgb() string.
function colorToCss(color) {
  if (color == null) return null;
  const argb = Number(color);
  if (!Number.isFinite(argb)) return null;
  const r = (argb >> 16) & 0xff;
  const g = (argb >> 8) & 0xff;
  const b = argb & 0xff;
  return `rgb(${r}, ${g}, ${b})`;
}

// Placeholder the API returns in place of a real formula for non-admin
// callers. Mirrors <HiddenFormulaPlaceholder> on SpreadsheetController
// and the constant on ModelSpreadsheet.js — kept in sync so the
// column-header tooltip never shows the formula even for stale payloads.
const HIDDEN_FORMULA_PLACEHOLDER = "<Hidden formula>";

const pageSizeOptions = [25, 50, 100, 500];

export const SharedSpreadsheets = () => {
  // Server hides formulas from non-admins. Mirror that here so the
  // column-header tooltip — built from the same payload — never
  // includes the formula either. The placeholder check is intentionally
  // strict ("<Hidden formula>" exactly) so a typo on the server side
  // would be caught rather than silently shown.
  const { user, isAuthenticated } = useAuth();
  const isAdmin = isAuthenticated && !!user?.Roles?.includes("Admin");

  // All shares the current user is granted access to (recipient side).
  const [shares, setShares] = useState([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [sharesError, setSharesError] = useState(null);

  // Models + users lookup, populated as needed for labels.
  const [models, setModels] = useState([]);
  const [usersBrief, setUsersBrief] = useState([]);

  // Selection state.
  const [selectedShareId, setSelectedShareId] = useState(null);
  const [recentCalculations, setRecentCalculations] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [selectedRecentId, setSelectedRecentId] = useState(null);

  // Result / pagination.
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    loadShares();
    loadModels();
    loadUsersBrief();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadUsersBrief() {
    try {
      const list = await ApiClient.getUsersBriefList();
      setUsersBrief(list || []);
    } catch (err) {
      console.error("Failed to load users", err);
      setUsersBrief([]);
    }
  }

  async function loadShares() {
    setLoadingShares(true);
    setSharesError(null);
    try {
      const data = await ApiClient.listSharesSharedWithMe();
      setShares(data || []);
    } catch (err) {
      console.error("Failed to load shares", err);
      setSharesError({
        message: err.message || "Unknown error",
        status: err.status || null,
      });
      setShares([]);
    } finally {
      setLoadingShares(false);
    }
  }

  async function loadModels() {
    try {
      const list = await ApiClient.getModelsList();
      setModels(list || []);
    } catch (err) {
      console.error("Failed to load models", err);
      setModels([]);
    }
  }

  // Group shares by model so the top-level picker is per-model.
  const sharesByModel = useMemo(() => {
    const map = new Map();
    for (const s of shares || []) {
      if (!map.has(s.ModelId)) map.set(s.ModelId, []);
      map.get(s.ModelId).push(s);
    }
    return map;
  }, [shares]);

  const selectedShare = useMemo(
    () => (shares || []).find((s) => s.Id === selectedShareId) || null,
    [shares, selectedShareId]
  );

  // When a share is selected:
  //   * legacy "model-wide" share (no SpreadsheetCalculationId) →
  //     populate the recent-calculation picker so the recipient picks
  //     one to view.
  //   * "Only this exactly calculation" pinned share →
  //     skip the picker entirely and load the pinned calc directly.
  useEffect(() => {
    if (!selectedShare) {
      setRecentCalculations([]);
      setSelectedRecentId(null);
      setResult(null);
      setError(null);
      setPage(0);
      return;
    }

    if (typeof selectedShare.SpreadsheetCalculationId === "number") {
      // Pinned share — render the pinned calc immediately, no picker.
      loadCalculation({ Id: selectedShare.SpreadsheetCalculationId });
    } else {
      loadRecentCalculations(selectedShare.ModelId);
    }
    setError(null);
    setPage(0);
  }, [selectedShareId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadRecentCalculations(modelId) {
    if (!modelId) return;
    setLoadingRecent(true);
    try {
      const data = await ApiClient.getRecentModelSpreadsheets(modelId, 20);
      setRecentCalculations(data?.Items || []);
    } catch (err) {
      console.error("Failed to load recent spreadsheets", err);
      setRecentCalculations([]);
    } finally {
      setLoadingRecent(false);
    }
  }

  async function loadCalculation(calc) {
    if (!calc || !calc.Id || !selectedShare) return;

    setLoading(true);
    setError(null);
    setSelectedRecentId(calc.Id);
    setPage(0);
    try {
      const data = await ApiClient.getModelSpreadsheetRowsFiltered(
        calc.Id,
        selectedShare.Id,
        { startRow: 0, limit: pageSize }
      );
      setResult({
        CalculationDbId: data.CalculationDbId,
        ColumnCount: data.ColumnCount,
        RowCount: data.RowCount,
        StartRow: 0,
        EndRow: data.Rows?.length || 0,
        ExecutionDuration: data.ExecutionDuration,
        Columns: data.Columns || [],
        Rows: data.Rows || [],
      });
    } catch (err) {
      console.error("Loading shared spreadsheet failed", err);
      setError({
        message: err.message || "Unknown error",
        structuredErrors: err.structuredErrors || null,
        status: err.status || null,
      });
    } finally {
      setLoading(false);
    }
  }

  async function fetchPage(newPage) {
    if (!selectedShare || !selectedRecentId) return;
    if (newPage < 0 || newPage >= totalPages) return;

    setLoading(true);
    setError(null);
    try {
      const data = await ApiClient.getModelSpreadsheetRowsFiltered(
        selectedRecentId,
        selectedShare.Id,
        { startRow: newPage * pageSize, limit: pageSize }
      );
      // Replace Rows with the new page only. We do NOT accumulate rows
      // from previously-visited pages: visibleRows used to slice the
      // accumulated array with `page * pageSize`, which silently produced
      // an empty result whenever the user jumped to a non-zero page
      // (the array never grew long enough to reach that offset).
      // See the comment on `visibleRows` for the bug this used to cause.
      setResult((prev) =>
        prev
          ? {
              ...prev,
              Rows: (data.Rows || []).sort(
                (a, b) => (a.Index ?? 0) - (b.Index ?? 0)
              ),
            }
          : prev
      );
      setPage(newPage);
    } catch (err) {
      console.error("Loading shared spreadsheet page failed", err);
      setError({
        message: err.message || "Unknown error",
        status: err.status || null,
      });
    } finally {
      setLoading(false);
    }
  }

  function onPageSizeChange(newSize) {
    setPageSize(newSize);
    setPage(0);
    setResult((prev) => (prev ? { ...prev, Rows: [] } : prev));
  }

  const totalRows = result?.RowCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  // Sorted visible rows for the current page.
  //
  // `result.Rows` always contains the rows for the *current* page only —
  // fetchPage replaces rather than accumulates (see below). We just need a
  // stable sort; no slicing. The previous "accumulate-then-slice" logic
  // was broken: visiting page 4 after page 0 left Rows = [page0, page4],
  // and visibleRows = sorted.slice(200, 250) on a 100-element array
  // returned []. The same bug was fixed in ModelSpreadsheet.js.
  const visibleRows = useMemo(() => {
    if (!result?.Rows) return [];
    return [...result.Rows].sort(
      (a, b) => (a.Index ?? 0) - (b.Index ?? 0)
    );
  }, [result]);

  function modelLabel(modelId) {
    const m = models.find((x) => x.Id?.toString() === modelId?.toString());
    if (!m) return `Model #${modelId}`;
    return m.Name || m.FileName || `Model #${modelId}`;
  }

  function userName(userId) {
    const u = usersBrief.find((x) => x.Id?.toString() === userId?.toString());
    if (!u) return `User #${userId}`;
    return u.Name || u.Email || `User #${userId}`;
  }

  return (
    <div>
      <h1>Shared spreadsheets</h1>
      <p className="text-muted">
        Browse models that other users have shared with you, pick a share, then
        pick a calculation and view only the columns that were granted.
      </p>

      {sharesError && (
        <div className="alert alert-danger">
          <h6>Failed to load shares</h6>
          <div>{sharesError.message}</div>
          {sharesError.status && (
            <small className="text-muted">HTTP Status: {sharesError.status}</small>
          )}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Models shared with you</h5>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={loadShares}
            disabled={loadingShares}
          >
            {loadingShares ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        <div className="card-body p-0">
          {loadingShares ? (
            <div className="p-3 text-muted">Loading shares…</div>
          ) : shares.length === 0 ? (
            <div className="p-3 text-muted">
              Nothing has been shared with you yet. Ask another user to create a
              share from the <em>Model Spreadsheet</em> page.
            </div>
          ) : (
            <table className="table table-sm table-hover mb-0 model-spreadsheet-recent-table">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Open</th>
                  <th>Share</th>
                  <th>Model</th>
                  <th>Columns</th>
                  <th>Shared by</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {Array.from(sharesByModel.entries()).flatMap(([modelId, modelShares]) =>
                  modelShares.map((s) => {
                    const isSelected = selectedShareId === s.Id;
                    return (
                      <tr
                        key={s.Id}
                        className={isSelected ? "table-active" : undefined}
                      >
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={() => setSelectedShareId(s.Id)}
                          >
                            Open
                          </button>
                        </td>
                        <td>{s.Name}</td>
                        <td>{modelLabel(modelId)} (ID: {modelId})</td>
                        <td>
                          {(s.SharedColumns || []).map((c) => (
                            <span
                              key={c}
                              className="badge badge-info mr-1"
                              style={{ marginRight: 4, color: "#000" }}
                            >
                              {c}
                            </span>
                          ))}
                        </td>
                        <td>{userName(s.CreatedByUserId)}</td>
                        <td>{formatDateTime(s.CreateTime)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selectedShare && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">
              Calculations for share "{selectedShare.Name}" — {modelLabel(selectedShare.ModelId)}
              {typeof selectedShare.SpreadsheetCalculationId === "number" && (
                <span
                  className="badge badge-warning ml-2"
                  style={{ color: "#000" }}
                  title="This share is pinned to one specific calculation — only that calculation is shown below."
                >
                  Pinned #{selectedShare.SpreadsheetCalculationId}
                </span>
              )}
            </h5>
            <small className="text-muted">
              {selectedShare.SharedColumns == null
                ? "Showing every column of the calculation."
                : `Showing only columns: ${(selectedShare.SharedColumns || []).join(", ")}`}
            </small>
          </div>
          <div className="card-body p-0">
            {typeof selectedShare.SpreadsheetCalculationId === "number" ? (
              <div className="p-3 text-muted">
                This share is pinned to calculation #
                {selectedShare.SpreadsheetCalculationId}. The view below
                is read-only and refreshes automatically if the share
                creator re-saves the same calculation.
              </div>
            ) : loadingRecent ? (
              <div className="p-3 text-muted">Loading calculations…</div>
            ) : recentCalculations.length === 0 ? (
              <div className="p-3 text-muted">
                No calculations available for this model yet.
              </div>
            ) : (
              <table className="table table-sm table-hover mb-0 model-spreadsheet-recent-table">
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>View</th>
                    <th>Calc #</th>
                    <th>Created</th>
                    <th>Duration</th>
                    <th>Columns × Rows</th>
                  </tr>
                </thead>
                <tbody>
                  {recentCalculations.map((calc) => {
                    const isSelected = selectedRecentId === calc.Id;
                    return (
                      <tr
                        key={calc.Id}
                        className={isSelected ? "table-active" : undefined}
                      >
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            disabled={loading}
                            onClick={() => loadCalculation(calc)}
                          >
                            View
                          </button>
                        </td>
                        <td>#{calc.Id}</td>
                        <td>{formatDateTime(calc.CreateTime)}</td>
                        <td>{formatDuration(calc.ExecutionDuration)}</td>
                        <td>
                          {calc.ColumnCount} × {calc.RowCount}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger">
          <h6>Failed to load shared spreadsheet</h6>
          <div>{error.message}</div>
          {error.status && (
            <small className="text-muted">HTTP Status: {error.status}</small>
          )}
        </div>
      )}

      {loading && !result && (
        <div className="alert alert-info">Loading…</div>
      )}

      {result && result.Columns && result.Columns.length > 0 && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
            <h5 className="mb-0">
              Shared spreadsheet
              {result.CalculationDbId && (
                <small className="text-muted ml-2">
                  · Calc #{result.CalculationDbId}
                </small>
              )}
            </h5>
            <div className="d-flex align-items-center">
              <span className="me-2">Rows per page:</span>
              <select
                className="form-control form-control-sm"
                style={{ width: 100 }}
                value={pageSize}
                onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
                disabled={loading}
              >
                {pageSizeOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="model-spreadsheet-scroll">
              <table className="table table-sm table-bordered mb-0 model-spreadsheet-table">
                <thead>
                  <tr>
                    <th className="row-index-col">#</th>
                    {result.Columns.map((col) => {
                      // The server substitutes the placeholder string in
                      // place of the real formula for non-admins. Treat
                      // the placeholder as "no formula" everywhere we
                      // render from the payload — including the
                      // column-header tooltip — so the formula never
                      // appears for non-admins even if the payload
                      // happens to be stale.
                      const rawFormula = col.Formula;
                      const hasRealFormula =
                        !!rawFormula &&
                        rawFormula !== HIDDEN_FORMULA_PLACEHOLDER &&
                        isAdmin;
                      const tagValue = col.Tag;
                      // Title shown on hover. Kept identical to before
                      // for admins so existing muscle memory still
                      // works; non-admins simply see the Tag line and
                      // never the formula line.
                      const tooltipTitle = `${col.Label}${col.Title ? " · " + col.Title : ""}${
                        hasRealFormula ? "\n" + rawFormula : ""
                      }${tagValue ? "\nTag: " + tagValue : ""}`;
                      return (
                        <th key={col.Index} title={tooltipTitle}>
                          <div className="model-spreadsheet-col-header">
                            <span className="model-spreadsheet-col-label">
                              {col.Label}
                            </span>
                            {col.Title && (
                              <span className="model-spreadsheet-col-title">
                                {col.Title}
                              </span>
                            )}
                            {tagValue && (
                              <span className="badge badge-info model-spreadsheet-col-tag">
                                {tagValue}
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr key={row.Index}>
                      <td className="row-index-col">{row.Index + 1}</td>
                      {(row.Cells || []).map((cell, ci) => {
                        const bg = colorToCss(cell.Color);
                        return (
                          <td
                            key={ci}
                            className="model-spreadsheet-cell"
                            style={bg ? { backgroundColor: bg } : undefined}
                          >
                            {cell.Text}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {visibleRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={result.Columns.length + 1}
                        className="text-center text-muted"
                      >
                        {loading ? "Loading…" : "No rows in the returned slice."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card-footer d-flex justify-content-between align-items-center flex-wrap">
            <div>
              Page {page + 1} of {totalPages} ({totalRows} rows)
              {loading && (
                <span className="ms-2 text-muted">Loading…</span>
              )}
            </div>
            <div className="btn-group">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === 0 || loading}
                onClick={() => fetchPage(0)}
              >
                «
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === 0 || loading}
                onClick={() => fetchPage(Math.max(0, page - 1))}
              >
                ‹ Prev
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page >= totalPages - 1 || loading}
                onClick={() => fetchPage(Math.min(totalPages - 1, page + 1))}
              >
                Next ›
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page >= totalPages - 1 || loading}
                onClick={() => fetchPage(totalPages - 1)}
              >
                »
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function formatDuration(duration) {
  if (duration == null) return "";
  if (typeof duration === "string") {
    const parts = duration.split(":");
    if (parts.length >= 3) {
      const hours = parseInt(parts[0], 10) || 0;
      const minutes = parseInt(parts[1], 10) || 0;
      const seconds = parseFloat(parts[2]) || 0;
      if (hours > 0) return `${hours}h ${minutes}m ${seconds.toFixed(1)}s`;
      if (minutes > 0) return `${minutes}m ${seconds.toFixed(1)}s`;
      if (seconds >= 1) return `${seconds.toFixed(2)}s`;
      return `${Math.round(seconds * 1000)}ms`;
    }
    return duration;
  }
  if (typeof duration === "number") {
    const totalMs = duration;
    if (totalMs >= 60_000) return `${(totalMs / 60_000).toFixed(1)} min`;
    if (totalMs >= 1_000) return `${(totalMs / 1_000).toFixed(2)}s`;
    return `${Math.round(totalMs)}ms`;
  }
  if (typeof duration === "object" && typeof duration.TotalMilliseconds === "number") {
    const totalMs = duration.TotalMilliseconds;
    if (totalMs >= 60_000) return `${(totalMs / 60_000).toFixed(1)} min`;
    if (totalMs >= 1_000) return `${(totalMs / 1_000).toFixed(2)}s`;
    return `${Math.round(totalMs)}ms`;
  }
  return String(duration);
}

function formatDateTime(value) {
  if (value == null) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}

export default SharedSpreadsheets;